<template>
  <div>
    <load-profile />
    <!-- Search Container Card -->
    <n-search-container
      :fields="searchFields"
      :default-visible="true"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      >
        <template #startDate>
          <div v-if="params.period != 'other'" />
        </template>
        <template #endDate>
          <div v-if="params.period != 'other'" />
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <b-card-title class="mb-1">
          {{ $t("general.detailReport") }}
        </b-card-title>
        <b-row>
          <b-col>
            <label>{{ $t("general.entries") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t("selection.noOption") }}
              </template>
            </v-select>
          </b-col>
          <b-col class="text-right">
            <b-button
              variant="success"
              @click="exportExcel"
            >
              {{ $t("button.exportExcel") }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :type="permissionType"
          :previewable="false"
          @sort-changed="sortChanged"
        >
          <template #cell(name)="data">
            <b-row align-v="center">
              <b-col cols="auto">
                <b-avatar
                  size="40"
                  variant="light-primary"
                  :src="data.item.profilePicture"
                  :text="avatarText(data.item.name)"
                >
                  <feather-icon
                    v-if="!data.item.name"
                    icon="UserIcon"
                    size="22"
                  />
                </b-avatar>
                <span class="ml-1"> {{ data.item.name }}</span>
              </b-col>
            </b-row>
          </template>
          <template #cell(satification)="data">
            {{ data.item.satification }}%
          </template>
          <template #cell(responseTime)="data">
            {{ toMinutesSeconds(data.item.responseTime) }}
          </template>
          <template #cell(percentage)="data">
            {{ data.item.percentage }}%
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BAvatar,
  BButton,
  BCardHeader,
  BCardTitle,
} from 'bootstrap-vue'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import NPagination from '@/components/NPagination'
import NTable from '@/components/NTable'
import vSelect from 'vue-select'
import { avatarText } from '@core/utils/filter'
import Repository from '@/repositories/RepositoryFactory'
import { calculateDatesByPeriod } from '@/libs/helper'
import moment from 'moment-timezone'
import TableFields from './tableFields'
import SearchInputs from './searchInput'

const TeamPerformanceRepository = Repository.get('teamPerformance')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BAvatar,
    BButton,
    BCardHeader,
    BCardTitle,
    NSearchContainer,
    NSearchInput,
    NPagination,
    NTable,
    vSelect,
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        period: this.$route.query.period || 'thisWeek',
        operator: this.$route.query.operator || null,
        startDate: this.$route.query.startDate || null,
        endDate: this.$route.query.endDate || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        period: this.$route.query.period || 'thisWeek',
        operator: this.$route.query.operator || null,
        startDate: this.$route.query.startDate || null,
        endDate: this.$route.query.endDate || null,
      },
      moment,
      avatarText,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
      currentLocale() {
        return this.$i18n.locale
      },
    },
  },
  watch: {
    perPage() {
      this.list()
    },
    'params.period': function (value) {
      this.calculateDate()
    },
  },
  mounted() {
    this.getData()
  },
  created() {
    this.a = document.createElement('a')
    document.body.appendChild(this.a)
    this.a.style = 'display: none'
  },
  methods: {
    toMinutesSeconds(totalSeconds) {
      const seconds = Math.floor(totalSeconds % 60)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600)
      const secondsStr = this.makeHumanReadable(seconds, 'second')
      const minutesStr = this.makeHumanReadable(minutes, 'minute')
      const hoursStr = this.makeHumanReadable(hours, 'hour')
      const string = `${hoursStr}${minutesStr}${secondsStr}`.replace(
        /,\s*$/,
        '',
      )

      return string || `0 ${this.$tc('time.second', 0)}`
    },
    makeHumanReadable(num, singular) {
      return num > 0 ? `${num} ${this.$tc(`time.${singular}`, num)} ` : ''
    },
    calculateDate() {
      let dates
      if (this.params.period == 'other') {
        dates = calculateDatesByPeriod('thisWeek')
      } else {
        dates = calculateDatesByPeriod(this.params.period)
      }
      if (dates.length) {
        this.params.startDate = dates[0]
        this.params.endDate = dates[1]
      }
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.params.period = 'thisWeek'
      this.calculateDate()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      if (!this.params.startDate || !this.params.endDate) {
        this.calculateDate()
        this.query = {
          ...this.query,
          ...this.params,
        }
      }
      TeamPerformanceRepository.index({
        ...this.query,
      })
        .then(response => {
          const data = response?.data?.data
          if (data) {
            this.items = [...data.list]
            this.total = data.total
          }
        })
        .catch(error => {
          this.loading = false
        })
        .then(() => {
          this.loading = false
        })
    },
    exportExcel() {
      this.loading = true
      TeamPerformanceRepository.exportExcel({
        ...this.query,
      })
        .then(response => {
          this.loading = false
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const url = window.URL.createObjectURL(blob)

          this.a.href = url
          this.a.download = `${this.$t(
            'general.teamPerformanceReport',
          )} ${moment().format('DD-MMM-YYYY')}.xlsx`
          this.a.click()
          window.URL.revokeObjectURL(url)
        })
        .catch(error => {
          this.loading = false
        })
        .then(() => {
          this.loading = false
        })
    },
  },
  setup() {
    const fields = TableFields
    const searchFields = SearchInputs
    const permissionType = 'report-team-performance'
    return {
      fields,
      searchFields,
      permissionType,
    }
  },
}
</script>
